// Bare `/w` landing — the workspace hub. `/w/<slug>` pins a tab to an org,
// but the bare prefix used to fall through the catch-all and render the
// shell 404 (2026-07-29 per Nika "prettify /w/ initial layout" — the demo
// entry link lands exactly here). Behavior:
//
//   - orgs still loading → quiet centered loader (no 404 flash)
//   - exactly ONE org    → immediate redirect to its `/w/<slug>` home
//   - several orgs       → a picker: one glass card per workspace, click
//                          routes into it (the AppOrgSwitcher stays the
//                          in-app switcher; this is only the URL landing)
//
// Uses `replaceState`-style routing (pushState: false) for the auto-forward
// so Back never bounces through the bare prefix.

// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import _wsShared from '@symbo-ls/workspace-shared'
const shellWithWorkspacePrefix = (...args) => _wsShared.functions.withWorkspacePrefix(...args)

const _hubOrgSlug = (o) => o?.slug || o?.username || null

// The orgs the hub OFFERS — the one org-visibility rule (shared
// functions/visibleOrgs.js, the same one the org switcher reads): a team
// member's own EMPTY personal org (/auth/me `hasContent: false`) stays in the
// background (POB persona round 5: a team sign-up saw "Choose a workspace"
// with an extra "My workspace"), but the org the person is IN is never
// hidden. A STALLED hub lists every org it knows: the rescue picker is
// the way out, and a hidden org must not be the one that was needed.
const _hubOrgs = (el, s) => {
  const root = s?.root
  const all = root?.orgs || []
  if (_hubStalled(el, s)) return all
  return el.call('visibleOrgs', all, { keep: [root?.org?.id, root?.org?.slug] })
}

// WS-CUSTOM-DOMAIN-HUB-SILENT-BOOT-FAIL-1 — "Opening your workspace…" was an
// UNCONDITIONAL, untimed promise. Every way the forward can fail to happen
// (no org resolvable, orgs never load, or a target that resolves right back to
// this page) left that sentence on screen forever, and `Cards` hid the picker
// below two orgs so a single-org user had no way out either.
//
// The boot resolver's own ladder is 25x200ms = 5s (functions/customDomainBoot.js),
// so a stall declared after 8s is strictly later than every source that could
// still legitimately arrive.
const _STALL_MS = 8000

// Injectable exactly like customDomainBoot.js's `delayMs`/`maxAttempts` ladder,
// so the stall can be asserted offline without an 8-second test.
const _stallMs = (el) => (typeof el.__wHubStallMs === 'number' ? el.__wHubStallMs : _STALL_MS)

const _hubStalled = (el, s) => !!(el.state?._stalled || s?._stalled)

// `withWorkspacePrefix(slug, '/')` emits `/w/<slug>` while the boot resolver
// and the address bar carry `/w/<slug>/` — the same place, spelled two ways.
// A trailing slash must never decide whether a forward is a self-forward.
const _samePath = (a, b) => {
  const trim = (p) => String(p || '').replace(/\/+$/, '') || '/'
  return trim(a) === trim(b)
}

const _forwardIfSingle = (el, s) => {
  const root = s.root
  const orgs = _hubOrgs(el, s)
  if (el.__wHubForwarded) return
  // One org → its home. Zero orgs but an active org resolved → same.
  const target = orgs.length === 1 ? orgs[0] : orgs.length === 0 ? root?.org : null
  const slug = _hubOrgSlug(target)
  if (!slug) return
  const path = shellWithWorkspacePrefix(slug, '/')
  // SELF-FORWARD GUARD. If this page is ALREADY mounted at the path it wants
  // to send the user to, routing there re-mounts this same page — a loop with
  // no exit that reads to the user as an eternal "one moment". Measured live
  // on app.bellforge.eu (2026-08-23, localStorage.mwt='off'): the hub rendered
  // at `/w/bellforge/` and forwarded to `/w/bellforge/`. The routing cause is
  // fixed in functions/mwtRouter.js; this stays as the invariant that no page
  // may forward to itself, whatever the router does upstream.
  if (_samePath(root?.currentPath, path)) {
    el.__wHubForwarded = true
    _markStalled(el)
    return
  }
  el.__wHubForwarded = true
  _clearStallTimer(el)
  el.router(path, el.getRoot(), {}, { pushState: false })
}

const _markStalled = (el) => {
  _clearStallTimer(el)
  if (el.state?._stalled) return
  el.state?.update({ _stalled: true })
}

const _clearStallTimer = (el) => {
  if (!el.__wHubStallTimer) return
  clearTimeout(el.__wHubStallTimer)
  el.__wHubStallTimer = null
}

const _armStallTimer = (el) => {
  if (el.__wHubForwarded || el.__wHubStallTimer || el.state?._stalled) return
  el.__wHubStallTimer = setTimeout(() => {
    el.__wHubStallTimer = null
    if (el.__wHubForwarded) return
    _markStalled(el)
  }, _stallMs(el))
}

// Retry = drop the one-shot guards and ask again, so a forward that failed on
// data that has since landed just works. When nothing has changed the stall
// timer re-arms and the failure state comes straight back — never a spinner
// that hides a second failure.
const _retryForward = (el, s) => {
  el.__wHubForwarded = false
  _clearStallTimer(el)
  el.state?.update({ _stalled: false })
  _forwardIfSingle(el, s)
  _armStallTimer(el)
}

export const workspaceHub = {
  extends: 'AppPage',
  metadata: (el) => ({ title: el.call('pageTitle', 'workspace.hub.page.title') }),
  flow: 'y',
  align: 'center center',
  minHeight: '100dvh',
  padding: 'C A',

  // Marker the Retry button's `lookup` predicate finds — see its onClick.
  __wHubPage: true,

  state: { _stalled: false },

  // Redirect must fire whenever orgs land — onRender covers the mount,
  // subscribeTo + onUpdate cover the async orgs arrival.
  subscribeTo: ['orgs', 'org', 'signedIn'],
  onRender: (el, s) => {
    _forwardIfSingle(el, s)
    _armStallTimer(el)
  },
  onUpdate: (el, s) => {
    _forwardIfSingle(el, s)
    _armStallTimer(el)
  },
  onRemove: (el) => _clearStallTimer(el),

  Hub: {
    flow: 'y',
    gap: 'B',
    width: '100%',
    maxWidth: 'I',
    align: 'center center',

    Intro: {
      flow: 'y',
      gap: 'Z',
      align: 'center center',
      textAlign: 'center',

      H: {
        tag: 'h1',
        margin: '0',
        fontSize: 'C',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const orgs = _hubOrgs(el, s)
          if (_hubStalled(el, s)) return el.call('polyglot', 'workspace.hub.stalledTitle')
          // A BRANDED TENANT HOST NEVER OFFERS A CHOICE — see `Cards.hide`.
          if (s.root?.whitelabelBrand) return el.call('polyglot', 'workspace.hub.opening')
          return el.call(
            'polyglot',
            orgs.length > 1 ? 'workspace.hub.choose' : 'workspace.hub.opening'
          )
        }
      },
      P: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => {
          const orgs = _hubOrgs(el, s)
          if (_hubStalled(el, s)) {
            return el.call(
              'polyglot',
              orgs.length ? 'workspace.hub.stalledWithOrgs' : 'workspace.hub.stalledNoOrgs'
            )
          }
          if (s.root?.whitelabelBrand) return el.call('polyglot', 'workspace.hub.oneMoment')
          if (orgs.length > 1) return el.call('polyglot', 'workspace.hub.pickToday')
          return el.call('polyglot', 'workspace.hub.oneMoment')
        }
      },

      // The one way out of the failure state. Hidden while the forward is
      // still plausibly in flight, so the happy path is unchanged.
      // FA806 — bare PascalCase key auto-extends the built-in; no `extends`.
      Button: {
        hide: (el, s) => !_hubStalled(el, s),
        marginTop: 'Z',
        text: '{{ workspace.hub.tryAgain | polyglot }}',
        onClick: (e, el, s) => {
          // `lookup` (ONE-argument ancestor predicate) is the DOMQL way up —
          // never a `.parent.parent` chain, which silently breaks the moment
          // this button moves a level (functions/posterCardHue.js precedent).
          const page = el.lookup((c) => !!c.__wHubPage)
          if (page) _retryForward(page, s)
        }
      }
    },

    Cards: {
      // A stalled hub shows EVERY workspace it knows, whatever the count —
      // the picker is the rescue affordance, and hiding it at <=1 was what
      // left a single-org user with nothing to click.
      //
      // WHITELABEL-HUB-PICKER-LEAKS-OTHER-ORGS-1 — but NOT on a branded tenant
      // host while the forward is still in flight. There the HOST names the
      // tenant (app.bellforge.eu -> the Bellforge org), so a picker is both the
      // wrong affordance and a LEAK: it lists every OTHER org the signed-in
      // user belongs to on someone else's branded domain. Measured 2026-08-23:
      // after signing in on app.bellforge.eu the user got "Choose a workspace"
      // instead of landing in Bellforge.
      //
      // A STALLED branded host still shows it — once the forward has provably
      // failed, a way out beats a clean surface, and that is the rescue this
      // block exists for.
      //
      // `whitelabelBrand` is the reactive marker the pre-auth brand chain
      // already writes onto root (whitelabelBrand.js), so this needs no new
      // state, no sibling import and no window.location read. Navigation stays
      // owned by bootCustomDomainScope — one writer per concern.
      hide: (el, s) =>
        _hubStalled(el, s)
          ? !_hubOrgs(el, s).length
          : !!s.root?.whitelabelBrand || _hubOrgs(el, s).length <= 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(var(--spacing-G1), 1fr))',
      gap: 'A',
      width: '100%',

      childExtends: 'WorkspaceHubCard',
      childrenAs: 'state',
      children: (el, s) => _hubOrgs(el, s).filter((o) => _hubOrgSlug(o))
    }
  }
}
